/**
 * LA GRÁFICA DEL HISTÓRICO DE MACROS Y PESO (pantalla 01 del documento de Jesús del 7-09,
 * regla 01·7, y pantalla 03).
 *
 * SE DIBUJA COMO LA DICTA EN EL VÍDEO, NO COMO LA ESCRIBIÓ EN EL DOCUMENTO. Son dos gráficas
 * distintas y él mismo dejó la elección abierta; la decisión de Francisco del 7-09 es el vídeo:
 *
 *      2:51  «las cajitas son los macros»
 *      2:54  «el peso es el puntito y los macros aquí salen sumando todos»
 *      2:59  «sale la cajita más alta, más baja»
 *
 * En el documento las barras eran solo los hidratos. Aquí son TODOS los macros del ajuste
 * sumados: los ocho números de esa fila en un solo número. No es lo que come en un día -- suma
 * los tres tipos de día, que no se viven a la vez --, así que no se rotula como si lo fuera:
 * es la altura del ajuste, para ver de un vistazo si le subieron o le bajaron. Los números de
 * verdad los da la tabla de debajo, que es la otra pregunta («la gráfica enseña la tendencia;
 * la tabla enseña los números»).
 *
 * Y la más alta y la más baja se marcan, que es la misma regla de siempre suya: en la tabla ya
 * se marcan el peso mínimo y el máximo.
 *
 * POR QUÉ VUELVE UNA GRÁFICA AQUÍ, que se quitó a propósito. La que se quitó el 17-08 era una
 * curva de PESO duplicada con la de Evolución, y las dos llegaron a decir cosas distintas del
 * mismo cliente el mismo día. Ésta no la duplica: el peso entra como referencia sobre el eje
 * de los macros -- para leer un ajuste contra lo que hizo el cuerpo -- y sale del mismo sitio
 * que la tabla, `macro_history`, así que no puede contradecirla.
 */
import React, { useMemo } from 'react';
import {
    Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { num1 } from '../lib/numeros';

const NARANJA = '#FF671F';
// Del tema y no a pelo, como en `GraficaDePeso`: a pelo no se leían en el lado del cliente.
const TENUE = 'hsl(var(--muted-foreground))';
const LINEA_TENUE = 'hsl(var(--border))';
const PESO = 'hsl(var(--foreground))';

// Cuántos ajustes entran. Doce cuentan una historia y siguen siendo legibles a 390 px; el que
// más tiene lleva 264 y a esa escala las barras son una mancha.
const AJUSTES_EN_LA_GRAFICA = 12;

// LOS OCHO NÚMEROS DEL AJUSTE, SUMADOS: «los macros aquí salen sumando todos». El peri no
// lleva grasas y las filas viejas ni siquiera lo llevan a él, así que lo que no está suma cero:
// es lo que dice esa fila, no un hueco que haya que rellenar.
const BLOQUES = [['entreno', ['proteina', 'hidratos', 'grasa']],
                 ['peri', ['proteina', 'hidratos']],
                 ['descanso', ['proteina', 'hidratos', 'grasa']]];

export const totalDeMacros = (h) => {
    let total = 0;
    for (const [bloque, campos] of BLOQUES) {
        for (const c of campos) {
            const v = h?.[bloque]?.[c];
            if (typeof v === 'number') total += v;
        }
    }
    return total;
};

const fechaEje = (iso) => (iso
    ? new Date(iso + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
    : '');

const Globo = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
            <p className="text-xs text-muted-foreground">{fechaEje(p.fecha)}</p>
            <p className="text-sm font-bold text-foreground leading-tight">
                {num1(p.total)} <span className="text-xs font-normal text-muted-foreground">g de macros</span>
            </p>
            {p.peso != null && (
                <p className="text-xs text-muted-foreground">{num1(p.peso)} kg</p>
            )}
        </div>
    );
};

const GraficaDeAjustes = ({ entradas }) => {
    // De la más vieja a la más nueva, que es como se lee una gráfica; las entradas llegan al
    // revés porque la tabla las enseña de la más nueva hacia abajo.
    const serie = useMemo(() => (entradas || [])
        .slice(0, AJUSTES_EN_LA_GRAFICA)
        .map(h => ({ fecha: h.fecha, total: totalDeMacros(h), peso: h.peso }))
        .filter(p => p.total > 0)
        .reverse(), [entradas]);

    // Con un solo ajuste no hay tendencia que dibujar, igual que la tabla no se pinta.
    if (serie.length < 2) return null;

    // «Sale la cajita mas alta, mas baja». Se marcan TODAS las que empatan en lo alto o en lo
    // bajo, no una elegida a dedo: con los mismos macros repetidos diez semanas, decir que la
    // mas alta es la tercera seria mentira. Y si todas valen igual no se marca ninguna: ahi no
    // hay ni mas alta ni mas baja.
    const totales = serie.map(p => p.total);
    const alto = Math.max(...totales);
    const bajo = Math.min(...totales);
    const marcada = (v) => alto !== bajo && (v === alto || v === bajo);

    return (
        <div className="surface p-3 pt-4" data-testid="mis-macros-grafica">
            <div className="h-[190px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={serie} barCategoryGap="18%"
                        margin={{ top: 6, right: 6, bottom: 0, left: -14 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={LINEA_TENUE} vertical={false} />
                        <XAxis dataKey="fecha" tickFormatter={fechaEje}
                            tick={{ fill: TENUE, fontSize: 10 }}
                            axisLine={false} tickLine={false} minTickGap={8} />
                        {/* EL EJE SE AJUSTA A LO QUE HAY (Jesús, 11-09, punto 13).
                            Su queja: «el eje llega a 1000 y tus ajustes suman unos 280; las
                            barras quedan pegadas al suelo y no se distingue una de otra».
                            Iba de cero al máximo, y con un historial donde hay apuntes de
                            1.836 g -- los suyos van de 280 a 1.836 -- las barras de ahora se
                            aplastan contra el suelo.
                            Ahora va de la más baja a la más alta con un poco de aire. Se
                            pierde el cero, que en una suma ayuda a no exagerar los cambios,
                            y se gana poder ver la tendencia, que es para lo que está. */}
                        <YAxis yAxisId="macros" width={46} tick={{ fill: TENUE, fontSize: 10 }}
                            domain={([min, max]) => {
                                const aire = Math.max(10, Math.round((max - min) * 0.15));
                                return [Math.max(0, Math.floor((min - aire) / 10) * 10),
                                        Math.ceil((max + aire) / 10) * 10];
                            }}
                            axisLine={false} tickLine={false} />
                        {/* SIN « kg» PEGADO AL NÚMERO: a 390 px no cabe «80 kg» en el hueco
                            que queda a la derecha y Recharts lo parte en dos renglones, «80»
                            encima de «kg», que es como salía. Los kilos los dice el pie. */}
                        <YAxis yAxisId="peso" orientation="right" width={34}
                            domain={['auto', 'auto']} tickFormatter={(v) => num1(v)}
                            tick={{ fill: TENUE, fontSize: 10 }} axisLine={false} tickLine={false} />
                        <Tooltip content={<Globo />} cursor={{ fill: LINEA_TENUE, fillOpacity: 0.3 }} />
                        {/* «Sale la cajita más alta, más baja»: las dos a color entero y el
                            resto en tono bajo, para que las dos se vean sin leer ningún número. */}
                        <Bar yAxisId="macros" dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={26}>
                            {serie.map((p, i) => (
                                <Cell key={i} fill={NARANJA} fillOpacity={marcada(p.total) ? 1 : 0.35} />
                            ))}
                        </Bar>
                        {/* «El peso es el puntito»: los puntos son lo que él nombra, y la línea
                            que los une va fina y por debajo, solo para poder seguirlos. */}
                        <Line yAxisId="peso" type="monotone" dataKey="peso" stroke={PESO}
                            strokeWidth={1} connectNulls
                            dot={{ fill: PESO, r: 3, strokeWidth: 0 }}
                            activeDot={{ r: 5, fill: PESO, stroke: 'hsl(var(--card))', strokeWidth: 2 }} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            {/* QUÉ ES CADA COSA, dicho aquí: una barra que suma los ocho números no se adivina,
                y sin esta línea alguien la leería como «lo que como en un día».

                Y CUÁNTOS AJUSTES SON, que no son los mismos que enseña la tabla de debajo: la
                gráfica va a doce y la tabla a cuatro, y sin decirlo cada una parecía contar mal
                lo de la otra. Lo de la más alta y la más baja solo se promete cuando de verdad
                hay alguna marcada: con todos los ajustes iguales no hay ni una ni otra. */}
            <p className="text-[11px] text-muted-foreground mt-1">
                Tus {serie.length} últimos ajustes. La barra suma los macros de ese ajuste y el
                punto es tu peso en kilos.{alto !== bajo && ' La más alta y la más baja van marcadas.'}
            </p>
        </div>
    );
};

export default GraficaDeAjustes;
